import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../app/z-store/store';

const PurchaseOrderInvoiceTab = () => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // State for data and UI
  const [searchTerm, setSearchTerm] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchInvoices();
    }
  }, [selectedFacilityId]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/app/purchase-order-invoices/all/${selectedFacilityId}`,
        'GET'
      );
      if (response.success) {
        setInvoices(response.data?.data || []);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = Array.isArray(invoices)
    ? invoices.filter((item) => {
        const poNumber = (item.poNumber || '').toLowerCase();
        const grnNumber = (item.grnNumber || '').toLowerCase();
        const amount = (item.amount || '').toString().toLowerCase();

        return (
          poNumber.includes(searchTerm.toLowerCase()) ||
          grnNumber.includes(searchTerm.toLowerCase()) ||
          amount.includes(searchTerm.toLowerCase())
        );
      })
    : [];

  // Date formatting function
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Currency formatting function
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Action template for datatable
  const actionTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-info p-button-sm"
          onClick={() => handleViewDetails(rowData)}
          tooltip="View Details"
        />
      </div>
    );
  };

  // Document template for datatable
  const documentTemplate = (rowData) => {
    if (rowData.document) {
      return (
        <Button
          icon="ti ti-download"
          className="p-button-rounded p-button-info p-button-sm"
          onClick={() => handleDownloadDocument(rowData.document)}
          tooltip="Download Document"
        />
      );
    }
    return <span className="text-muted">No document</span>;
  };

  const handleViewDetails = (invoiceData) => {
    toast.current.show({
      severity: 'info',
      summary: 'View Details',
      detail: `Viewing details for Invoice: ${invoiceData.grnNumber}`,
    });
  };

  const handleDownloadDocument = (documentUrl) => {
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  if (!selectedFacilityId) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="alert alert-warning" role="alert">
            <i className="ti ti-alert-triangle me-2"></i>
            Please select a facility to view invoices.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />

      <div className="card">
        <div className="card-body">
          {/* Search */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">
                  <i className="ti ti-search"></i>
                </span>
                <InputText
                  placeholder="Search by PO number, GRN, amount..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                />
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="table-responsive">
            <DataTable
              value={filteredInvoices}
              emptyMessage="No invoices found."
              sortMode="multiple"
              paginator
              rows={10}
              stripedRows
              loading={loading}
              tableStyle={{ minWidth: '50rem' }}
              stateStorage="session"
              stateKey="purchase-order-invoices-state"
            >
              <Column
                field="date"
                header="DATE"
                sortable
                body={(rowData) => formatDate(rowData.date)}
              />
              <Column field="poNumber" header="Purchase Order" sortable />
              <Column field="grnNumber" header="GRN Number" sortable />
              <Column
                field="quantity"
                header="Quontity"
                sortable
                body={(rowData) => rowData.quantity?.toLocaleString() || '0'}
              />
              <Column
                field="amount"
                header="Amount"
                sortable
                body={(rowData) => formatCurrency(rowData.amount)}
              />
              <Column
                field="status"
                header="Payment Status"
                sortable
                // body={(rowData) => formatCurrency(rowData.amount)}
              />
              <Column
                header="Actions"
                body={actionTemplate}
                style={{ width: '8rem' }}
              />
            </DataTable>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderInvoiceTab;